import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CustomEase } from 'gsap/CustomEase';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { baseViewHeight, fontColorHex, logoCssDistance, proteinEvent, proteinPointDistance, proteinPoints, ratioThreshold, } from '../ShaderAnimation/animationUtils';
import { MOBILE_SAFE_RESIZE } from '../globalUtils';
export var LogoAnimationState;
(function (LogoAnimationState) {
    LogoAnimationState["PointsIntro"] = "points_intro";
    LogoAnimationState["PointsOutro"] = "points_outro";
    LogoAnimationState["PointsToLogo"] = "points_to_logo";
    LogoAnimationState["LogoToPoints"] = "logo_to_points";
    LogoAnimationState["LogoReveal"] = "logo_reveal";
    LogoAnimationState["Bespoke"] = "bespoke";
    LogoAnimationState["None"] = "none";
})(LogoAnimationState || (LogoAnimationState = {}));
// we project the animation in this size then scale it with css;
const fixedSize = 1024;
const pointOutPosition = -2300;
const outMoveRatio = -0.5;
const wiggle = 0.5;
const wiggleRatio = wiggle;
const pointEaseOut = CustomEase.create('custom', 'M0,0 C0.18,0 0.286,-0.478 0.504,-0.478 0.863,-0.478 0.818,1.001 1,1 ');
const pointEaseIn = CustomEase.create('custom', 'M0,0 C0.049,0.51 0.129,1.263 0.448,1.264 0.707,1.264 0.658,1 1,1 ');
const pointWiggleIn = CustomEase.create('custom', 'M0,0 C0.374,0 0.3,1 1,1 ');
const pointWiggleOut = CustomEase.create('custom', 'M0,0 C0.206,0 0.138,1.417 0.408,1.437 0.596,1.45 0.604,1 1,1 ');
const baseScale = 9.7;
const heightRotation = 90;
const linesRaw = [
    {
        rotation: 10.83 - heightRotation,
        center: { x: -23.1, y: 118.6 },
        width: 324,
    },
    {
        rotation: 31.49 - heightRotation,
        center: { x: -62.51, y: 101.5 },
        width: 326,
    },
    {
        rotation: 52.88 - heightRotation,
        center: { x: -90.3, y: 68.4 },
        width: 334,
    },
    {
        rotation: -52.88 + heightRotation,
        center: { x: 90.3, y: 68.4 },
        width: 334,
    },
    {
        rotation: -31.49 + heightRotation,
        center: { x: 62.51, y: 101.5 },
        width: 326,
    },
    {
        rotation: -10.83 + heightRotation,
        center: { x: 23.1, y: 118.6 },
        width: 324,
    },
];
const LogoAnimation = ({ state, eventsOnParent, isBespoke, }) => {
    const mounted = useRef(false);
    const stateRef = useRef(state);
    const prevStateRef = useRef(state);
    const pointRefs = useRef([...proteinPoints]);
    const insideRefs = useRef([...proteinPoints]);
    const inLinePos = useRef(proteinPoints.map(() => 0));
    const delayedPointsRef = useRef(null);
    const ratioRefs = useRef([
        ...proteinPoints.map(() => ({
            value: 0,
        })),
    ]);
    const logoLines = useRef(proteinPoints.map((_, idx) => {
        let rotation = 0;
        return { width: 0, rotation: 0, center: { x: 0, y: 0 } };
    }));
    const svgRef = useRef(null);
    const circleRef = useRef(null);
    const circumferenceRef = useRef(0);
    const strokeWidthRef = useRef(0);
    const projection = useRef({ width: 0, height: 0 });
    const containerRef = useRef(null);
    const fixedRatio = useRef(1);
    const scaleRatio = useRef(1);
    const registerPointRef = (idx, ref) => {
        pointRefs.current[idx] = ref;
    };
    const registerInsideRef = (idx, ref) => {
        insideRefs.current[idx] = ref;
    };
    useEffect(() => {
        window.addEventListener(MOBILE_SAFE_RESIZE, onResize);
        onResize();
        setShapesDefault();
        return () => {
            window.removeEventListener(MOBILE_SAFE_RESIZE, onResize);
            delayedPointsRef.current?.kill();
        };
    }, []);
    useEffect(() => {
        prevStateRef.current = stateRef.current;
        stateRef.current = state;
        if (mounted.current) {
            applyAnimation(state, (state === LogoAnimationState.PointsIntro ||
                state === LogoAnimationState.PointsOutro) &&
                prevStateRef.current !== LogoAnimationState.LogoReveal
                ? 0.6
                : 0);
        }
    }, [state]);
    const onResize = () => {
        requestAnimationFrame(applyPositions);
    };
    const logoBaseHeight = 790;
    const minPointHeight = 0.5;
    const applyPositions = () => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds)
            return;
        const lvh = window.visualViewport?.height || window.innerHeight;
        const scale = lvh / bounds.height;
        projection.current = {
            width: bounds.width * scale,
            height: bounds.height * scale,
        };
        const { width, height } = projection.current;
        const aspect = width / height;
        let effectiveViewHeight;
        if (aspect < ratioThreshold) {
            effectiveViewHeight =
                (baseViewHeight / (aspect / ratioThreshold)) *
                    (logoBaseHeight / height);
        }
        else {
            effectiveViewHeight = baseViewHeight * (logoBaseHeight / height);
        }
        fixedRatio.current = proteinPointDistance / fixedSize;
        scaleRatio.current = fixedSize / effectiveViewHeight;
        console.log(scaleRatio.current);
        // const distance = proteinPointDistance * effectiveViewHeight * 0.00059;
        const distance = fixedRatio.current * logoCssDistance;
        const pointsWidth = (proteinPoints.length - 1) * distance;
        inLinePos.current = inLinePos.current.map((_, idx) => idx * distance - pointsWidth / 2);
        logoLines.current = logoLines.current.map((_, idx) => {
            let width = 0;
            let rotation = 0;
            let center = { x: 0, y: 0 };
            const raw = linesRaw[idx];
            width = raw.width * fixedRatio.current * baseScale;
            rotation = raw.rotation;
            center = {
                x: raw.center.x * fixedRatio.current * baseScale,
                y: raw.center.y * fixedRatio.current * baseScale,
            };
            return { width, rotation, center };
        });
        if (stateRef.current === LogoAnimationState.PointsIntro ||
            stateRef.current === LogoAnimationState.PointsOutro) {
            const pointSize = basePointSize * fixedRatio.current;
            pointRefs.current.forEach((element, idx) => {
                if (!element)
                    return;
                gsap.set(element, {
                    x: inLinePos.current[idx],
                    y: 0,
                    rotation: 0,
                });
            });
            insideRefs.current.forEach((element, idx) => {
                if (!element)
                    return;
                gsap.set(element, {
                    x: '-50%',
                    y: '-50%',
                    attr: { 'stroke-width': pointSize },
                    height: minPointHeight,
                });
            });
        }
        resizeCircle(pointsWidth);
        if (stateRef.current === LogoAnimationState.LogoReveal) {
            pointRefs.current.forEach((element, idx) => {
                if (!element)
                    return;
                gsap.set(element, {
                    x: logoLines.current[idx].center.x,
                    y: logoLines.current[idx].center.y,
                });
            });
            insideRefs.current.forEach((element, idx) => {
                if (!element)
                    return;
                gsap.set(element, {
                    height: logoLines.current[idx].width,
                    attr: { 'stroke-width': strokeWidthRef.current },
                });
            });
        }
        setShapesDefault(true);
        if (!mounted.current) {
            pointRefs.current.forEach((element, idx) => {
                if (!element)
                    return;
                gsap.set(element, { y: 0 });
            });
            applyAnimation(stateRef.current);
        }
        if (containerRef.current) {
            containerRef.current.style.transform = `scale(${scaleRatio.current})`;
        }
        mounted.current = true;
    };
    const onMessage = () => {
        containerRef.current?.parentElement?.dispatchEvent(new CustomEvent(proteinEvent, { detail: ratioRefs.current }));
    };
    const resizeCircle = (size) => {
        if (!circleRef.current)
            return;
        strokeWidthRef.current = size / 400;
        const radius = (size - strokeWidthRef.current) / 2;
        circumferenceRef.current = 2 * Math.PI * radius;
        // Update the circle geometry
        circleRef.current.setAttribute('cx', `${fixedSize / 2}`);
        circleRef.current.setAttribute('cy', `${fixedSize / 2}`);
        circleRef.current.setAttribute('r', `${radius}`);
        circleRef.current.setAttribute('r', `${radius}`);
        circleRef.current.setAttribute('stroke-width', `${strokeWidthRef.current}`);
        /* circleRef.current.setAttribute('stroke-dasharray', `${circumference}`);
          circleRef.current.setAttribute('stroke-dashoffset', `${circumference}`); */
        circleRef.current.style.transformOrigin = `${fixedSize / 2}px ${fixedSize / 2}px`;
    };
    const setShapesDefault = (onlyCircle) => {
        if (circleRef.current) {
            circleRef.current.setAttribute('stroke-dasharray', `${circumferenceRef.current}`);
            circleRef.current.setAttribute('stroke-dashoffset', `${circumferenceRef.current}`);
            gsap.set(circleRef.current, {
                strokeDashoffset: stateRef.current === LogoAnimationState.LogoReveal
                    ? 0
                    : circumferenceRef.current,
            });
        }
        if (onlyCircle)
            return;
        gsap.set(circleRef.current, { rotate: -90 });
        pointRefs.current.forEach((element, idx) => {
            if (!element)
                return;
            gsap.set(element, { y: 0 });
        });
        insideRefs.current.forEach((element, idx) => {
            if (!element)
                return;
            gsap.set(element, {
                scale: 0,
            });
        });
    };
    const isCircleShown = useRef(false);
    const basePointSize = 240;
    const applyAnimation = (s, delay = 0.6) => {
        const outPos = pointOutPosition * fixedRatio.current;
        const pointSize = basePointSize * fixedRatio.current;
        if (circleRef.current) {
            if (s === LogoAnimationState.LogoReveal) {
                isCircleShown.current = true;
                gsap.to(circleRef.current, {
                    strokeDashoffset: 0,
                    duration: 0.9,
                    delay,
                    rotate: -270,
                    ease: 'power2.inOut',
                });
            }
            else if (isCircleShown.current) {
                isCircleShown.current = false;
                gsap.to(circleRef.current, {
                    strokeDashoffset: circumferenceRef.current,
                    duration: 0.5,
                    delay,
                    rotate: -90,
                    ease: 'power2.0ut',
                });
            }
        }
        pointRefs.current.forEach((element) => {
            gsap.killTweensOf(element);
        });
        insideRefs.current.forEach((element) => {
            gsap.killTweensOf(element);
        });
        ratioRefs.current.forEach((element) => {
            gsap.killTweensOf(element);
        });
        if (!isBespoke) {
            if (s === LogoAnimationState.PointsIntro) {
                startPointsAnimation(true);
            }
            else {
                stopPointsAnimation();
            }
        }
        switch (s) {
            case LogoAnimationState.PointsIntro:
                pointRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    if (prevStateRef.current !== LogoAnimationState.LogoReveal) {
                        gsap.set(element, {
                            y: outPos,
                        });
                    }
                    const duration = 0.8 + idx * 0.1;
                    gsap.to(element, {
                        duration,
                        delay,
                        y: 0,
                        x: inLinePos.current[idx],
                        rotation: 0,
                        ease: pointEaseIn,
                    });
                    if (eventsOnParent) {
                        const onUpdate = idx === pointRefs.current.length - 1 ? onMessage : undefined;
                        const ratio = ratioRefs.current[idx];
                        ratio.value = 1;
                        gsap.to(ratio, {
                            duration,
                            delay,
                            value: 0,
                            ease: pointEaseIn,
                            onUpdate,
                            onComplete: onUpdate,
                        });
                    }
                });
                insideRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration: 0.6,
                        delay,
                        scale: 1,
                        x: '-50%',
                        y: '-50%',
                        attr: { 'stroke-width': pointSize },
                        height: minPointHeight,
                        ease: 'power2.out',
                    });
                });
                if (eventsOnParent) {
                    onMessage();
                }
                break;
            case LogoAnimationState.PointsOutro:
                pointRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    const duration = 0.86 + idx * 0.1;
                    gsap.to(element, {
                        duration,
                        y: outPos * outMoveRatio,
                        x: inLinePos.current[idx],
                        rotation: 0,
                        ease: pointEaseOut,
                    });
                    if (eventsOnParent) {
                        const onUpdate = idx === pointRefs.current.length - 1 ? onMessage : undefined;
                        const ratio = ratioRefs.current[idx];
                        gsap.to(ratio, {
                            duration,
                            value: outMoveRatio,
                            ease: pointEaseOut,
                            onUpdate,
                            onComplete: onUpdate,
                        });
                    }
                });
                insideRefs.current.forEach((element) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration: 0.6,
                        delay: 0.2,
                        scale: 0,
                        x: '-50%',
                        y: '-50%',
                        attr: { 'stroke-width': pointSize },
                        height: minPointHeight,
                        ease: 'power3.in',
                    });
                });
                if (eventsOnParent) {
                    onMessage();
                }
                break;
            case LogoAnimationState.LogoReveal:
                const shortTime = 0.4;
                const longTime = 1.1;
                const longEase = 'power2.out';
                pointRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration: longTime,
                        delay,
                        rotation: logoLines.current[idx].rotation,
                        x: logoLines.current[idx].center.x,
                        y: logoLines.current[idx].center.y,
                        ease: longEase,
                    });
                });
                insideRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration: shortTime,
                        delay,
                        scale: 1,
                        x: '-50%',
                        y: '-50%',
                        attr: { 'stroke-width': strokeWidthRef.current },
                        ease: 'power2.out',
                    });
                    gsap.to(element, {
                        duration: longTime,
                        delay,
                        height: logoLines.current[idx].width,
                        ease: longEase,
                    });
                });
                break;
            case LogoAnimationState.None:
                const duration = 0.6;
                const ease = 'power2.inOut';
                pointRefs.current.forEach((element, idx) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration,
                        y: 0,
                        x: inLinePos.current[idx],
                        rotation: 0,
                        ease,
                    });
                    if (eventsOnParent) {
                        const onUpdate = idx === pointRefs.current.length - 1 ? onMessage : undefined;
                        const ratio = ratioRefs.current[idx];
                        gsap.to(ratio, {
                            duration,
                            value: 0,
                            ease,
                            onUpdate,
                            onComplete: onUpdate,
                        });
                    }
                });
                insideRefs.current.forEach((element) => {
                    if (!element)
                        return;
                    gsap.to(element, {
                        duration,
                        scale: 0,
                        x: '-50%',
                        y: '-50%',
                        attr: { 'stroke-width': strokeWidthRef.current },
                        height: minPointHeight,
                        ease,
                    });
                });
                if (eventsOnParent) {
                    onMessage();
                }
                break;
        }
    };
    const pointsDelay = 4;
    const startPointsAnimation = (initial) => {
        delayedPointsRef.current?.kill();
        delayedPointsRef.current = gsap.delayedCall(initial ? 1 : pointsDelay, onPointsAnimation);
    };
    const stopPointsAnimation = () => {
        delayedPointsRef.current?.kill();
    };
    const onPointsAnimation = () => {
        startPointsAnimation();
        const outPos = pointOutPosition * fixedRatio.current * wiggleRatio;
        pointRefs.current.forEach((element, idx) => {
            if (!element)
                return;
            const allDuration = 1.8 + idx * 0.2;
            const duration = allDuration * 0.3;
            const delay = 0;
            const durationOut = allDuration - duration;
            const delayOut = duration;
            gsap.to(element, {
                duration,
                delay,
                y: outPos,
                ease: pointWiggleIn,
            });
            gsap.to(element, {
                duration: durationOut,
                delay: delayOut,
                y: 0,
                ease: pointWiggleOut,
            });
            if (eventsOnParent) {
                const onUpdate = idx === pointRefs.current.length - 1 ? onMessage : undefined;
                const ratio = ratioRefs.current[idx];
                ratio.value = 0;
                gsap.to(ratio, {
                    duration,
                    delay,
                    value: wiggleRatio,
                    ease: pointWiggleIn,
                    onUpdate,
                    onComplete: onUpdate,
                });
                gsap.to(ratio, {
                    duration: durationOut,
                    delay: delayOut,
                    value: 0,
                    ease: pointWiggleOut,
                    onUpdate,
                    onComplete: onUpdate,
                });
            }
        });
    };
    return (_jsxs("div", { className: "logo-animation", ref: containerRef, children: [_jsx("div", { className: "logo-animation-center", children: proteinPoints.map((_, idx) => {
                    return (_jsx("div", { className: "logo-animation-point", ref: (ref) => registerPointRef(idx, ref), children: _jsx("svg", { width: "0.1", height: "1000", className: "logo-animation-point-inside", ref: (ref) => registerInsideRef(idx, ref), strokeWidth: "3.3", children: _jsx("line", { x1: "0", y1: "0", x2: "0", y2: "100%", stroke: "var(--fontColor)" }) }) }, idx));
                }) }), _jsx("svg", { className: "logo-animation-svg", ref: svgRef, children: _jsx("circle", { className: "logo-animation-circle", ref: circleRef, stroke: fontColorHex, fill: "none", strokeLinecap: "round" }) })] }));
};
export default LogoAnimation;
